import {
  FFEScheduleFinancials,
  FFEScheduleItem,
  FFEScheduleItemPricing,
} from "ts-interfaces";

// Derived money is computed here at render time and NEVER persisted — the doc
// stores only the user-entered pricing inputs (see FFEScheduleItemPricing).
//
// The model follows Programa's documented schedule semantics
// (help.programa.com.au, "How product pricing calculations work in Schedules"):
//   cost   = trade price, falling back to RRP when no trade is known
//   markup = applied on cost (trade preferred, RRP fallback). Unset markup
//            displays as "—" but computes as 0: with no markup and no entered
//            client price the client simply pays cost — trade passthrough,
//            or RRP for catalog-seeded items
//   qty    = a positive number, or blank. Programa has no qty-0 state — a
//            typed 0 is normalized to blank, and blank means "not decided":
//            extendeds show "—" and the item stays out of the totals
//            ("Without a quantity, cost calculations cannot proceed"). Add
//            flows seed quantity 1, so blank only happens when a user
//            deliberately clears or zeroes the field.
// Resolved percentages are the EFFECTIVE values in play — back-computed when
// the user entered the other side of the pair — so displays never show a
// stored markup/discount the math didn't actually use.

export type CostBasis = "trade" | "discount" | "rrp";

export interface ResolvedPricing {
  listPrice?: number; // per unit (MSRP / RRP)
  trade?: number; // per unit real trade cost (entered, or list × (1 − discount))
  cost?: number; // per unit cost basis: trade, else RRP (Programa "Cost")
  costBasis?: CostBasis;
  client?: number; // per unit client price
  markupPct?: number; // effective markup between cost and client
  tradeDiscountPct?: number; // effective discount between list and trade
  quantity?: number; // positive, or undefined = blank ("not decided") — no extendeds
  extendedList?: number;
  extendedTrade?: number; // real trade only — stays undefined on RRP fallback
  extendedCost?: number;
  extendedClient?: number;
  profit?: number; // extendedClient − extendedCost
  savings?: number; // extendedList − extendedClient
  savingsPct?: number; // unit-based, so a qty-0 row still shows it
  tradeIsDerived: boolean;
  clientIsDerived: boolean;
}

const num = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined;

// Percentage inputs are non-negative by construction (the inputs reject
// "-"); a negative stored value is corrupt data and reads as unset — it must
// never flip totals negative.
const pos = (v: unknown): number | undefined => {
  const n = num(v);
  return n !== undefined && n >= 0 ? n : undefined;
};

// Quantity must be positive: a stored 0 collapses to blank (Programa
// normalizes a typed 0 the same way), and negative/junk values from a bad
// doc read as blank too — never as negative money.
export const normalizeQuantity = (v: unknown): number | undefined => {
  const n = num(v);
  return n !== undefined && n > 0 ? n : undefined;
};

// A discount is only meaningful in [0, 100]: legacy docs may hold >100, and
// a derived trade cost must floor at 0 rather than go negative. Applied on
// read (legacy docs) and on write (setPricingField) so fresh stores and old
// docs resolve identically.
const clampDiscountPct = (n: number): number => Math.min(Math.max(n, 0), 100);

// A real price is a positive finite amount — catalog metadata uses 0 as a
// "price unknown" placeholder, and negative money is corrupt. Shared by the
// engine's read side (posMoney) and the catalog-seeding guard in
// ffeSchedulesApi.
export const isRealPrice = (v: unknown): v is number => {
  const n = num(v);
  return n !== undefined && n > 0;
};

// Stored money must be a positive amount in whole cents: sub-cent entries
// normalize so visible unit × qty arithmetic reconciles, and a stored 0 or
// negative price is placeholder/corrupt data that reads as unset (catalog
// metadata uses 0 for "price unknown"; a genuinely free item is expressed as
// a 100% trade discount).
const posMoney = (v: unknown): number | undefined => {
  if (!isRealPrice(v)) return undefined;
  const r = roundMoney(v);
  return r > 0 ? r : undefined;
};

// Round to cents once per line so the rows a user sees sum exactly to the
// totals row (raw float sums drift). Normalizes −0 so Intl never renders
// "-$0.00".
export function roundMoney(v: number): number {
  const rounded = Math.round(v * 100) / 100;
  return rounded === 0 ? 0 : rounded;
}

const extend = (
  unit: number | undefined,
  qty: number | undefined,
): number | undefined =>
  unit !== undefined && qty !== undefined ? roundMoney(unit * qty) : undefined;

export function resolveItemPricing(
  item: FFEScheduleItem,
  financials?: FFEScheduleFinancials,
): ResolvedPricing {
  const p = item.pricing || {};
  const quantity = normalizeQuantity(item.quantity);
  const listPrice = posMoney(p.listPrice);

  const discountInput =
    pos(p.tradeDiscountPct) ?? pos(financials?.defaultTradeDiscountPct);
  const enteredTrade = posMoney(p.tradePrice);
  const discountPct =
    discountInput !== undefined ? clampDiscountPct(discountInput) : undefined;
  // Derived units round to cents immediately — the unit a user SEES times the
  // quantity must equal the extended figure to the cent.
  const trade =
    enteredTrade !== undefined
      ? enteredTrade
      : listPrice !== undefined && discountPct !== undefined
        ? roundMoney(listPrice * (1 - discountPct / 100))
        : undefined;

  // Programa's "Cost": the trade price when known, else the RRP.
  const cost = trade ?? listPrice;
  const costBasis: CostBasis | undefined =
    enteredTrade !== undefined
      ? "trade"
      : trade !== undefined
        ? "discount"
        : listPrice !== undefined
          ? "rrp"
          : undefined;

  const markupInput = pos(p.markupPct) ?? pos(financials?.markupPct);
  const enteredClient = posMoney(p.clientPrice);
  // Unset markup computes as 0 (Programa shows "—" but treats it as 0), so a
  // priced row ALWAYS has a client price: the client pays cost until a markup
  // or a direct client price says otherwise. Without this, a trade-only
  // section totals to $0 despite real quantities and prices.
  const clientFromMarkup =
    cost !== undefined
      ? roundMoney(cost * (1 + (markupInput ?? 0) / 100))
      : undefined;
  const client = enteredClient ?? clientFromMarkup;

  // The markup actually in play: the input when it produced the client price,
  // back-computed when the client price was entered directly, and absent
  // otherwise — an unset markup displays "—", not a phantom 0%.
  const markupPct =
    enteredClient !== undefined
      ? cost !== undefined && cost > 0
        ? (enteredClient / cost - 1) * 100
        : undefined
      : markupInput !== undefined && clientFromMarkup !== undefined
        ? markupInput
        : undefined;

  // Effective discount off RRP — needs both sides (Programa: "both values
  // need to be entered"); a missing trade must never read as a discount.
  const tradeDiscountPct =
    trade !== undefined && listPrice !== undefined && listPrice > 0
      ? (1 - trade / listPrice) * 100
      : undefined;

  const extendedList = extend(listPrice, quantity);
  const extendedTrade = extend(trade, quantity);
  const extendedCost = extend(cost, quantity);
  const extendedClient = extend(client, quantity);
  const profit =
    extendedClient !== undefined && extendedCost !== undefined
      ? roundMoney(extendedClient - extendedCost)
      : undefined;
  const savings =
    extendedList !== undefined && extendedClient !== undefined
      ? roundMoney(extendedList - extendedClient)
      : undefined;
  // Unit-based (Programa: (RRP − client) ÷ RRP), so it survives qty 0.
  const savingsPct =
    listPrice !== undefined && listPrice > 0 && client !== undefined
      ? ((listPrice - client) / listPrice) * 100
      : undefined;

  return {
    listPrice,
    trade,
    cost,
    costBasis,
    client,
    markupPct,
    tradeDiscountPct,
    quantity,
    extendedList,
    extendedTrade,
    extendedCost,
    extendedClient,
    profit,
    savings,
    savingsPct,
    tradeIsDerived: enteredTrade === undefined,
    clientIsDerived: enteredClient === undefined,
  };
}

export interface ScheduleTotals {
  list: number;
  cost: number; // trade where known, RRP fallback otherwise (matches "Cost" cells)
  client: number;
  profit: number; // Σ of per-item profits that are defined — NOT client − cost
  // Savings need an RRP baseline, so both aggregate ONLY over items that have
  // both a list and a client extended — a client-price-only item must not
  // read as a negative saving.
  savings?: number;
  savingsPct?: number;
  tradeDiscountPct?: number; // over items with a real trade AND a list price
}

export function scheduleTotals(
  items: FFEScheduleItem[],
  financials?: FFEScheduleFinancials,
): ScheduleTotals {
  let list = 0;
  let cost = 0;
  let client = 0;
  let profit = 0;
  // The discount aggregate only holds where a real trade AND a list price
  // exist — a missing trade must never read as a 100% discount. Savings hold
  // the same way: only where a list AND a client price exist.
  let discountedList = 0;
  let discountedTrade = 0;
  let savingsList = 0;
  let savingsClient = 0;
  let hasSavingsBasis = false;
  for (const item of items) {
    const r = resolveItemPricing(item, financials);
    list += r.extendedList ?? 0;
    cost += r.extendedCost ?? 0;
    client += r.extendedClient ?? 0;
    // Sum only defined per-item profits: an item with a client price but no
    // cost (or vice versa) has UNKNOWN profit and must not distort the total,
    // so this is deliberately not client − cost.
    if (r.profit !== undefined) profit += r.profit;
    if (r.extendedTrade !== undefined && r.extendedList !== undefined) {
      discountedTrade += r.extendedTrade;
      discountedList += r.extendedList;
    }
    if (r.extendedList !== undefined && r.extendedClient !== undefined) {
      savingsList += r.extendedList;
      savingsClient += r.extendedClient;
      hasSavingsBasis = true;
    }
  }
  list = roundMoney(list);
  cost = roundMoney(cost);
  client = roundMoney(client);
  profit = roundMoney(profit);
  savingsList = roundMoney(savingsList);
  savingsClient = roundMoney(savingsClient);
  return {
    list,
    cost,
    client,
    profit,
    savings: hasSavingsBasis
      ? roundMoney(savingsList - savingsClient)
      : undefined,
    savingsPct:
      savingsList > 0
        ? ((savingsList - savingsClient) / savingsList) * 100
        : undefined,
    tradeDiscountPct:
      discountedList > 0
        ? (1 - discountedTrade / discountedList) * 100
        : undefined,
  };
}

export type PricingField = keyof FFEScheduleItemPricing;

// The two mutually exclusive input pairs the schema mandates: entering a
// tradePrice clears tradeDiscountPct (and vice versa — list×discount derives
// trade); entering clientPrice clears markupPct (and vice versa).
const EXCLUSIVE_PAIR: Partial<Record<PricingField, PricingField>> = {
  tradePrice: "tradeDiscountPct",
  tradeDiscountPct: "tradePrice",
  clientPrice: "markupPct",
  markupPct: "clientPrice",
};

export function setPricingField(
  pricing: FFEScheduleItemPricing | undefined,
  field: PricingField,
  value: number | undefined,
): FFEScheduleItemPricing | undefined {
  const next: FFEScheduleItemPricing = { ...(pricing || {}) };
  if (value === undefined) {
    delete next[field];
  } else {
    // Store what the math will use, never a display-only "150%".
    next[field] =
      field === "tradeDiscountPct" ? clampDiscountPct(value) : value;
    const paired = EXCLUSIVE_PAIR[field];
    if (paired) delete next[paired];
  }
  // An empty object would persist as a useless key — strip to undefined so
  // the whole-doc save drops `pricing` entirely.
  return Object.keys(next).length > 0 ? next : undefined;
}

// Parse a percent text input: '' → undefined (clear), invalid/negative →
// null (caller ignores). A typed 0 is a REAL value, not a clear: 0% discount
// pins trade = RRP and 0% markup pins client = cost — distinct from unset,
// which falls back to the sheet default (or derives nothing). Like any other
// typed percentage, a stored 0 replaces the field's exclusive pair.
export function parsePercentInput(raw: string): number | undefined | null {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  const n = parseFloat(trimmed.replace(/,/g, ""));
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

// Parse a money text input: '' or a typed 0 → undefined (clear). A 0 price
// is the catalog's "price unknown" placeholder, never real money (a free
// item is expressed as a 100% trade discount), and this matters for data
// safety: clearing NEVER touches the field's exclusive pair, while storing
// a real 0 would delete it. Invalid input returns null (caller ignores).
export function parsePricingInput(raw: string): number | undefined | null {
  const n = parsePercentInput(raw);
  return n === 0 ? undefined : n;
}

// Parse a quantity text input. A typed 0 normalizes to blank exactly like
// Programa does (their editor flips a 0 to "—" on autosave): blank, 0,
// negative, or junk all clear the field, which blanks the row's extended
// figures and drops it from totals ("Without a quantity, cost calculations
// cannot proceed"). Unit prices stay visible either way.
export function parseQuantityInput(raw: string): number | undefined {
  const n = parsePricingInput(raw);
  return typeof n === "number" ? n : undefined;
}

const moneyFormatters = new Map<string, Intl.NumberFormat>();

// sessionCurrency is seeded from the profile country, so guard against a
// code Intl doesn't recognize rather than throwing mid-render.
export function formatMoney(
  value: number | undefined,
  currency?: string,
): string {
  if (value === undefined) return "—";
  const code = currency || "USD";
  let fmt = moneyFormatters.get(code);
  if (!fmt) {
    try {
      fmt = new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: code,
      });
    } catch {
      fmt = new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "USD",
      });
    }
    moneyFormatters.set(code, fmt);
  }
  return fmt.format(value);
}

export function formatPct(value: number | undefined): string {
  if (value === undefined) return "—";
  return `${value.toFixed(2)}%`;
}

// At rest, an editable money input shows the RESOLVED value — what the math
// actually used — falling back to the raw stored entry so a value never looks
// wiped. One rule for both editor surfaces (grid row and expand tab).
export function displayMoney(
  resolved: number | undefined,
  stored: number | undefined,
  currency?: string,
): string {
  const v = resolved ?? stored;
  return v != null ? formatMoney(v, currency) : "";
}

// Bare-number variant for editable % fields (the input renders its own "%"):
// the EFFECTIVE percentage in play when one exists, else the raw stored
// input. Shares formatPct's 2-decimal precision.
export function displayPct(
  effective: number | undefined,
  stored: number | undefined,
): string {
  if (effective != null) return effective.toFixed(2);
  return stored != null ? String(stored) : "";
}

// Qty cells across every view show a positive quantity or blank — exactly
// how the engine reads it (a stored 0/junk/non-finite qty is "not decided").
export function displayQuantity(quantity: unknown): string {
  const n = normalizeQuantity(quantity);
  return n !== undefined ? String(n) : "";
}
